import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { LocalStorageSettingsService } from 'src/app/shared/services/local-storage-settings.service';
import { ReportingServiceService } from 'src/app/shared/services/reporting-service.service';

type LogicOp = 'AND' | 'OR';
type SetOp = 'UNION' | 'UNION ALL' | 'INTERSECT' | 'MINUS';

interface Condition {
  column: string;
  conditionalOperator: string; // =, !=, >, <, IN, LIKE, IS NULL, ...
  value: string;
  operator?: LogicOp; // operator BEFORE this condition except first
}

interface ConditionGroup {
  groupOperator?: LogicOp; // operator between this group and previous item
  conditions: Condition[];
}

interface ParsedJoin {
  type: string;
  table: string;
  alias: string;
  on: string;
}

interface ParsedSelect {
  rawSelectItems: string[];
  baseTable: string;
  baseAlias: string;
  joins: ParsedJoin[];
  wherePart: string;
  whereTopNonGrouped: Array<{ opBefore?: LogicOp; text: string }>;
  whereTopGroups: Array<{ opBefore?: LogicOp; text: string }>;
  groupByPart?: string;
}

// Represents a single item in the SELECT clause
interface SelectItem {
  type: 'column' | 'expression';
  // For column mode
  column?: string;           // base column name (e.g., 'drug_name')
  aggregate?: string;        // 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX' or empty
  distinct?: boolean;        // only applicable when aggregate is set
  // For expression mode
  expression?: string;       // free-form SQL expression
  // Common
  alias?: string;            // optional alias
}

// For JOINs we need the same structure
interface JoinSelectItem extends SelectItem {}


@Component({
    selector: 'app-sql-query-builder',
    templateUrl: './sql-query-builder.component.html',
    styleUrls: ['./sql-query-builder.component.scss'],
    standalone: false
})
export class SqlQueryBuilderComponent implements OnInit, OnChanges {

  // ✅ import binding for union child + parent import panel
  @Input() importedSql: string = '';

  @Input() showUnions: boolean;
  @Input() unionIndex: number;
  @Input() queryType: string; // normal | subQuery | unionQuery
  @Input() objectList: any[];
  @Input() editQuery:string;
  @Input() fromJSIPFormulaBuilder: boolean = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() parentValueChange = new EventEmitter<string>();
  @Output() unionQueryChange = new EventEmitter<any>();

  // UI Models
  nonGroupedConditions: Condition[] = [];
  groups: ConditionGroup[] = [];

  tables: any[] = [];
  columns: any[] = [];
  attributesList: any[] = [];

  objectAlias = '';
  selectedCommand = '';
  selectUnionType: SetOp | '' = '';

  selectedTables: string[] = [];
  selectedColumns: SelectItem[] = [];
  selectedJoins: any[] = [];

  // ✅ we keep these aligned by index always
  selectedUnions: any[] = [];          // placeholder rows for UI
  selectedUnionQueries: string[] = []; // the actual union SQL segments emitted/ imported

  sqlQuery: string = '';
  subSqlQuery: string = '';
  selectedObject = '';

  buttonColor = '';
  joinTypes = ['INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN'];

  groupConcatColumn: string = '';
  useGroupConcat: boolean = false;

  // Import UI
  importSqlText: string = '';
  importErrors: string[] = [];
  groupByColumns: string[] = [];

  private lastImportedSqlApplied = ''; // prevent loops
private pendingImportSql: string = '';
private isTablesReady = false;

  constructor(
    private localStorageSettings: LocalStorageSettingsService,
    private service: ReportingServiceService,
    private spinner: NgxUiLoaderService,
    private snackbar: MatSnackBar
  ) {}

 ngOnInit(): void {
  this.tables = this.objectList || [];
  console.log("object list in sql builder",this.tables);
  this.isTablesReady = this.tables.length > 0;

  this.buttonColor = this.localStorageSettings.getSettings("ColorA");
  this.pendingImportSql=this.editQuery;
  console.log(this.editQuery);
  
  // ✅ if import came early, apply now
  if (this.pendingImportSql) {
    this.importSqlToBuilder(this.pendingImportSql);
    this.pendingImportSql = '';
  } else if (this.importedSql?.trim()) {
    this.importSqlToBuilder(this.importedSql);
  }
}


 ngOnChanges(changes: SimpleChanges): void {

  // ✅ When objectList arrives later
  if (changes['objectList']) {
    this.tables = this.objectList || [];
    this.isTablesReady = this.tables.length > 0;
    console.log("object list in sql builder",this.tables);

    // if import waiting, apply now
    if (this.isTablesReady && this.pendingImportSql) {
      this.importSqlToBuilder(this.pendingImportSql);
      this.pendingImportSql = '';
    }
  }

  // ✅ When importedSql arrives (union child)
  if (changes['importedSql'] && this.importedSql?.trim()) {
    if (!this.isTablesReady) {
      // defer
      this.pendingImportSql = this.importedSql;
      return;
    }
    this.importSqlToBuilder(this.importedSql);
  }
}


  private applyImportedSqlOnce(sql: string) {
    const norm = this.normalizeSql(sql);
    if (!norm || norm === this.lastImportedSqlApplied) return;
    this.lastImportedSqlApplied = norm;
    this.importSqlToBuilder(norm);
  }

  /* ============================================================
   * ✅ Core SQL Builder
   * ============================================================ */

  generateQuery(): void {
    if (!this.selectedTables || !this.selectedTables.length) {
      this.sqlQuery = '';
      this.emitAll();
      return;
    }

    const baseIsSubQuery = this.selectedTables[0] === 'addFromQuery';
    const baseAlias = this.cleanAlias(this.objectAlias);
    const baseQualifier = this.cleanAlias(this.objectAlias) ? `${this.cleanAlias(this.objectAlias)}.` : '';

  // Build SELECT items from selectedColumns (base table)
  const selectParts: string[] = [];

  this.selectedColumns.forEach(item => {
    let expr = '';
    if (item.type === 'column' && item.column) {
      let colExpr = this.qualifyColumn(item.column, baseQualifier);
      if (item.aggregate) {
        const distinct = item.distinct ? 'DISTINCT ' : '';
        colExpr = `${item.aggregate}(${distinct}${colExpr})`;
      }
      expr = colExpr;
    } else if (item.type === 'expression' && item.expression) {
      expr = item.expression.trim();
    }

    if (expr && item.alias && item.alias.trim()) {
      expr += ` AS ${this.cleanAlias(item.alias)}`;
    }

    if (expr) selectParts.push(expr);
  });

  // If nothing selected, default to '*'
  if (selectParts.length === 0) {
    selectParts.push('*');
  }

  // Add join columns (simple columns, no aggregates for now)
  this.selectedJoins?.forEach((join: any, idx: number) => {
    if (!join.table || !join.type) return;

    if (!join.alias) join.alias = `t${idx + 1}`;
    const joinQualifier = this.cleanAlias(join.alias) + '.';

    const joinCols = join.selectedColumns?.length ? join.selectedColumns : [];
    joinCols.forEach((col: string) => {
      if (col === '*') {
        selectParts.push(joinQualifier + '*');
      } else {
        selectParts.push(joinQualifier + col);
      }
    });
  });

  const selectClause = selectParts.join(', ');

    // FROM
    let query = '';

    if (baseIsSubQuery) {
      console.log(this.selectedCommand);
      
      query = `${this.selectedCommand} ${selectClause} FROM (${this.subSqlQuery || ''})`;
    } else {
      if (this.queryType === 'unionQuery') {
        const from = baseAlias
          ? `FROM ${this.selectedTables[0]} AS ${baseAlias}`
          : `FROM ${this.selectedTables[0]}`;
        query = `\n${this.selectUnionType || 'UNION'} \n(SELECT ${selectClause} ${from}`;
      } else {
        const from = baseAlias
          ? `FROM ${this.selectedTables[0]} AS ${baseAlias}`
          : `FROM ${this.selectedTables[0]}`;
        query = `${this.selectedCommand} ${selectClause} ${from}`;
      }
    }

    // JOINS
    this.selectedJoins?.forEach((join: any, idx: number) => {
      if (!join?.table || !join?.type || !join?.onCondition) return;

      if (!join.alias || !String(join.alias).trim()) join.alias = `t${idx + 1}`;
      query += ` ${join.type} ${join.table} AS ${this.cleanAlias(join.alias)} ON ${join.onCondition}`;
    });

    // WHERE
    if (this.groups.length || this.nonGroupedConditions.length) {
      const whereClause = this.buildWhereClause();
      if (whereClause.trim()) query += ` WHERE ${whereClause}`;
    }

    if (this.groupByColumns.length > 0) {
    const groupCols = this.groupByColumns.map(col => this.qualifyColumn(col, baseQualifier)).join(', ');
    query += ` GROUP BY ${groupCols}`;
  }

    // SET operators (union chain)
    // We store each union piece already containing "\nUNION \n(SELECT ...)"
    // ✅ SET operators (union chain) - do NOT keep wrapping
if (this.selectedUnionQueries?.length) {
  // wrap base SELECT once
  let chained = this.wrapOnce(query);

  this.selectedUnionQueries.forEach(u => {
    const piece = (u || '').trim();
    if (!piece) return;
    chained += ` ${piece}`;
  });

  query = chained;
}


    if (this.queryType === 'unionQuery') query += ')';

    this.sqlQuery = query;
    this.emitAll();
  }
private wrapOnce(sql: string): string {
  const s = (sql || '').trim();
  if (!s) return '()';
  if (s.startsWith('(') && s.endsWith(')')) return s; // already wrapped once
  return `(${s})`;
}
  buildWhereClause(): string {
    const pieces: string[] = [];

    const nonGrouped = this.nonGroupedConditions
      .map((cond, i) => `${i > 0 ? (cond.operator || 'AND') + ' ' : ''}${this.formatCondition(cond)}`)
      .filter(Boolean)
      .join(' ');
    if (nonGrouped.trim()) pieces.push(nonGrouped.trim());

    const grouped = this.groups
      .map((g) => {
        const inner = (g.conditions || [])
          .map((c, i) => `${i > 0 ? (c.operator || 'AND') + ' ' : ''}${this.formatCondition(c)}`)
          .filter(Boolean)
          .join(' ');
        if (!inner.trim()) return '';
        return `${g.groupOperator ? g.groupOperator + ' ' : ''}(${inner.trim()})`;
      })
      .filter(Boolean)
      .join(' ');
    if (grouped.trim()) pieces.push(grouped.trim());

    return pieces.join(' ').trim();
  }

  private formatCondition(cond: Condition): string {
    const col = (cond.column || '').trim();
    const op = (cond.conditionalOperator || '').trim().toUpperCase();
    const raw = (cond.value ?? '').toString().trim();

    // raw fallback when import cannot map complex expressions
    if (!op) return col;

    if (!col) return '';

    if (op === 'IS NULL' || op === 'IS NOT NULL') return `${col} ${op}`;

    if (op === 'IN') {
      const items = raw
        .split(',')
        .map(x => x.trim())
        .filter(Boolean)
        .map(x => this.formatValueLiteral(x));
      return `${col} IN (${items.join(', ')})`;
    }

    return `${col} ${op} ${this.formatValueLiteral(raw)}`;
  }

  private formatValueLiteral(v: string): string {
    if (v === '') return `''`;
    if (/^-?\d+(\.\d+)?$/.test(v)) return v;

    const upper = v.toUpperCase();
    if (upper === 'NULL') return 'NULL';

    // keep quoted inputs but normalize to single quotes
    if ((v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))) {
      const inner = v.substring(1, v.length - 1).replace(/'/g, "''");
      return `'${inner}'`;
    }
    return `'${v.replace(/'/g, "''")}'`;
  }

  private formatSelectExpr(qualifier: string, col: string): string {
    if (!col) return '';
    if (col === '*') return qualifier ? `${qualifier}*` : '*';

    if (this.useGroupConcat && this.groupConcatColumn && col === this.groupConcatColumn) {
      const expr = qualifier ? `${qualifier}${col}` : col;
      return `GROUP_CONCAT(${expr} SEPARATOR ',') AS ${col}_CONCAT`;
    }
    return qualifier ? `${qualifier}${col}` : col;
  }

  private cleanAlias(a: string): string {
    return (a || '').trim();
  }

  /* ============================================================
   * ✅ UI actions
   * ============================================================ */

  addJoin() {
    this.selectedJoins.push({ table: '', alias: '', selectedColumns: [], type: '', onCondition: '', objectId: '', attributesList: [] });
  }

  removeJoin(index: number) {
    this.selectedJoins.splice(index, 1);
    this.generateQuery();
  }

  addUnion() {
    // ✅ keep arrays aligned
    this.selectedUnions.push({});
    this.selectedUnionQueries.push('');
    this.generateQuery();
  }

  // ✅ IMPORTANT:
  // When this component is a UNION CHILD (queryType === 'unionQuery'),
  // the remove button should ask parent to delete this child row.
  removeUnion(index: number) {
    if (this.queryType === 'unionQuery') {
      // parent will remove when sqlQuery empty
      this.unionQueryChange.emit({ sqlQuery: '', unionIndex: this.unionIndex });
      return;
    }

    // Parent removing its union row (rare in your UI)
    this.selectedUnions.splice(index, 1);
    this.selectedUnionQueries.splice(index, 1);
    this.generateQuery();
  }

  addNonGroupCondition(): void {
    const operator = this.nonGroupedConditions.length > 0 ? 'AND' : undefined;
    this.nonGroupedConditions.push({ column: '', conditionalOperator: '=', value: '', operator });
    this.generateQuery();
  }

  removeNonGroupCondition(index: number): void {
    this.nonGroupedConditions.splice(index, 1);
    if (this.nonGroupedConditions.length) this.nonGroupedConditions[0].operator = undefined;
    this.generateQuery();
  }

  addGroup(): void {
    const groupOperator = (this.nonGroupedConditions.length || this.groups.length) ? 'AND' : undefined;
    this.groups.push({ groupOperator, conditions: [{ column: '', conditionalOperator: '=', value: '' }] });
    this.generateQuery();
  }

  addCondition(groupIndex: number): void {
    const group = this.groups[groupIndex];
    const operator = group.conditions.length > 0 ? 'AND' : undefined;
    group.conditions.push({ column: '', conditionalOperator: '=', value: '', operator });
    this.generateQuery();
  }

  removeCondition(groupIndex: number, condIndex: number): void {
    this.groups[groupIndex].conditions.splice(condIndex, 1);
    if (this.groups[groupIndex].conditions.length) this.groups[groupIndex].conditions[0].operator = undefined;
    this.generateQuery();
  }

  removeGroup(index: number): void {
    this.groups.splice(index, 1);
    this.generateQuery();
  }

  emitValue() { this.valueChange.emit(this.sqlQuery); }
  emitParentValue() { this.parentValueChange.emit(this.sqlQuery); }
  emitUnionQuery() { this.unionQueryChange.emit({ sqlQuery: this.sqlQuery, unionIndex: this.unionIndex }); }

  private emitAll() {
    this.emitValue();
    this.emitUnionQuery();
    this.emitParentValue();
  }

  handleChildValue(value: string) {
    this.subSqlQuery = value;
    this.generateQuery();
  }

  // ✅ Parent receives union child SQL and stores into selectedUnionQueries[idx]
  handleUnionQueries(value: any) {
    if (!value) return;

    const idx = value.unionIndex;

    if (!value.sqlQuery || !String(value.sqlQuery).trim()) {
      this.selectedUnionQueries.splice(idx, 1);
      this.selectedUnions.splice(idx, 1);
    } else {
      this.selectedUnionQueries[idx] = value.sqlQuery;
      // ensure placeholder row exists
      if (!this.selectedUnions[idx]) this.selectedUnions[idx] = {};
    }

    this.generateQuery();
  }

  /* ============================================================
   * Object selection API calls (your existing behavior)
   * ============================================================ */

  onObjectSelectionChange(event: any) {
    if (!event?.value) return;

    if (event.value !== 'addFromQuery') {
      const obj = (this.tables || []).find(item => item.objectId == event.value);
      this.selectedTables[0] = obj?.objectName || '';

      //if used By Jsip Formula Builder then do not call API to fetch columns and directly generate query
        if (this.fromJSIPFormulaBuilder) {
          console.log(obj);
          this.columns = obj?.columns || [];
          this.attributesList = obj?.columns || [];
          this.generateQuery();
          return;
        }


      this.spinner.start();
      this.service.retrieveTablesFieldsForDataset(event.value).subscribe((res: any) => {
        if (res?.error === true) {
          this.attributesList = [];
          this.columns = [];
          this.spinner.stop();
          this.snackbar.open(res.message, 'OK', { duration: 5000 });
          return;
        }

        try {
          const json = JSON.parse(res[0].attributeNameAndFields);
          const cols = json?.datasets ? json.datasets[0].columns : json;
          this.attributesList = cols || [];
          this.columns = cols || [];
        } catch (e) {
          this.attributesList = [];
          this.columns = [];
        } finally {
          this.spinner.stop();
          this.generateQuery();
        }
      });
    } else {
      this.columns = [];
      this.selectedTables[0] = 'addFromQuery';
      this.generateQuery();
    }
  }

  onJoinObjectSelectionChange(event: any, index: number) {
    const obj = (this.tables || []).find(item => item.objectId == event.value);
    this.selectedJoins[index].table = obj?.objectName || '';

      if (this.fromJSIPFormulaBuilder) {
        console.log(obj);
        this.selectedJoins[index].attributesList = obj?.columns || [];
        this.generateQuery();
        return;
      }

    this.spinner.start();
    this.service.retrieveTablesFieldsForDataset(event.value).subscribe((res: any) => {
      if (res?.error === true) {
        this.selectedJoins[index].attributesList = [];
        this.spinner.stop();
        this.snackbar.open(res.message, 'OK', { duration: 5000 });
        return;
      }

      try {
        const json = JSON.parse(res[0].attributeNameAndFields);
        const cols = json?.datasets ? json.datasets[0].columns : json;
        this.selectedJoins[index].attributesList = cols || [];
      } catch (e) {
        this.selectedJoins[index].attributesList = [];
      } finally {
        this.spinner.stop();
        this.generateQuery();
      }
    });
  }

  /* ============================================================
   * ✅ COMPLETE IMPORT: joins + set operators + top-level groups
   * ============================================================ */
importSqlToBuilder(sql: string) {
  this.importErrors = [];

  const normalized = this.normalizeSql(sql);
  if (!normalized) {
    this.importErrors.push('SQL is empty.');
     this.snackbar.open('SQL is empty.', 'OK', { duration: 5000 });
    
    return;
  }

  const asUnionChild = (this.queryType === 'unionQuery');
  let childOp: SetOp | '' = '';

  let workingSql = normalized;

  if (asUnionChild) {
    const m = workingSql.toUpperCase().match(/^\s*(UNION ALL|UNION|INTERSECT|MINUS)\s*/);
    if (m) {
      childOp = m[1] as SetOp;
      workingSql = workingSql.substring(m[0].length).trim();
    }

    // ✅ unwrap ALL layers, not just one
    workingSql = this.unwrapAllOuterParens(workingSql);

    this.selectUnionType = childOp || this.selectUnionType || 'UNION';
  }

  // ✅ unwrap parent too (helps when SQL starts with ((SELECT...) UNION ...))
  workingSql = this.unwrapAllOuterParens(workingSql);

  const setParts = asUnionChild
    ? [{ op: undefined, sql: workingSql }]
    : this.splitTopLevelSetOperators(workingSql);

  if (!setParts.length) {
    this.importErrors.push('Unable to detect SELECT statement.');
     this.snackbar.open('Unable to detect SELECT statement.', 'OK', { duration: 5000 });
    return;
  }

  // ✅ unwrap first part before parsing
  const firstSql = this.unwrapAllOuterParens(setParts[0].sql);
  const first = this.parseSingleSelect(firstSql);
  if (!first) {
    this.importErrors.push('Failed to parse the first SELECT. (Maybe subquery FROM is used)');
    this.snackbar.open('Failed to parse the first SELECT. (Maybe subquery FROM is used)', 'OK', { duration: 5000 });
    return;
  }

  this.bindParsedSelect(first);

  if (!asUnionChild) {
    this.selectedUnions = [];
    this.selectedUnionQueries = [];

    for (let i = 1; i < setParts.length; i++) {
      const op = setParts[i].op as SetOp;

      // ✅ unwrap each union piece before wrapping again
      const pieceSql = this.unwrapAllOuterParens(setParts[i].sql);

      this.selectedUnions.push({});
      this.selectedUnionQueries.push(this.wrapAsUnionPiece(op, pieceSql));
    }
  }

  this.generateQuery();
}
private unwrapAllOuterParens(sql: string): string {
  let s = (sql || '').trim();
  while (this.isFullyWrappedByParens(s)) {
    s = s.slice(1, -1).trim();
  }
  return s;
}

private isFullyWrappedByParens(sql: string): boolean {
  const s = (sql || '').trim();
  if (!(s.startsWith('(') && s.endsWith(')'))) return false;

  let depth = 0;
  let inQuote = false;

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "'" && s[i - 1] !== '\\') inQuote = !inQuote;
    if (inQuote) continue;

    if (ch === '(') depth++;
    else if (ch === ')') depth--;

    // if we close the outer parens before the last char => not fully wrapped
    if (depth === 0 && i < s.length - 1) return false;
  }
  return depth === 0;
}


private bindParsedSelect(p: ParsedSelect) {
  // reset state
  this.selectedColumns = [];
  this.selectedJoins = [];
  this.nonGroupedConditions = [];
  this.groups = [];
  this.groupByColumns = [];

  if ((p as any).groupByPart) {
  const cols = this.splitTopLevelComma((p as any).groupByPart);

      this.groupByColumns = cols.map(col => {
        const raw = col.trim().replace(/`/g, '');

        // remove table alias ONLY (tdc.id → id)
        const withoutAlias = raw.includes('.') 
          ? raw.split('.').slice(1).join('.') 
          : raw;

        // now try exact match with column.name
        const found = this.columns.find(c =>
          c.name.toLowerCase() === withoutAlias.toLowerCase()
        );

        return found ? found.name : withoutAlias;
      });
      console.log('Parsed GROUP BY columns:', this.groupByColumns);
    }

  // command
  if (this.queryType !== 'unionQuery') this.selectedCommand = 'SELECT';

  // ✅ SUBQUERY FROM binding
  if ((p as any).fromIsSubquery) {
    this.selectedTables = ['addFromQuery'];
    this.selectedObject = 'addFromQuery';

    this.subSqlQuery = (p as any).fromSubquerySql || '';
    this.objectAlias = p.baseAlias || ''; // outer alias (a1)

    // Outer columns: if SQL is "a1.*" show "*"
    this.selectedColumns = p.rawSelectItems.map(raw => this.parseSelectItem(raw));

    // outer columns unknown for "Add From Query"
    this.columns = [];

    // WHERE (outer where)
    p.whereTopNonGrouped?.forEach((b, idx) => {
      const c = this.parseSingleConditionBestEffort(b.text);
      c.column = this.normalizeColumnRef(c.column);
      if (idx > 0 && b.opBefore) c.operator = b.opBefore;
      this.nonGroupedConditions.push(c);
    });
    if (this.nonGroupedConditions.length) this.nonGroupedConditions[0].operator = undefined;

    p.whereTopGroups?.forEach((b) => {
      const inner = this.unwrapOuterParens(b.text);
      const parts = this.splitTopLevelLogical(inner);

      const group: ConditionGroup = { groupOperator: b.opBefore, conditions: [] };
      parts.forEach((pt, idx) => {
        const c = this.parseSingleConditionBestEffort(pt.text);
        c.column = this.normalizeColumnRef(c.column);
        if (idx > 0 && pt.opBefore) c.operator = pt.opBefore;
        group.conditions.push(c);
      });
      if (group.conditions.length) group.conditions[0].operator = undefined;
      this.groups.push(group);
    });

    // ✅ IMPORTANT: stop here; do NOT execute normal table binding
    this.generateQuery();
    return;
  }

  // ✅ NORMAL TABLE binding
  this.selectedTables = [p.baseTable];
  this.objectAlias = p.baseAlias || '';

  // map base select items to base vs join using alias
  p.joins.forEach((j, idx) => {
    this.selectedJoins.push({
      table: j.table,
      alias: j.alias || `t${idx + 1}`,
      selectedColumns: [],
      type: j.type,
      onCondition: j.on,
      objectId: '',
      attributesList: []
    });
  });

  const baseAlias = (p.baseAlias || '').toLowerCase();
  const joinAliasMap = new Map<string, any>();
  this.selectedJoins.forEach((j: any) => joinAliasMap.set(String(j.alias || '').toLowerCase(), j));

  // Filter out items that actually belong to joins
const finalBaseColumns: SelectItem[] = [];
p.rawSelectItems.forEach(rawItem => {
  const cleaned = this.cleanSelectExprToColumn(rawItem);
  const simple = cleaned.match(/^([A-Za-z0-9_]+)\.([A-Za-z0-9_*]+)$/);
  
  if (simple) {
    const alias = simple[1].toLowerCase();
    const col = simple[2];
    
    if (joinAliasMap.has(alias)) {
      // This belongs to a join - add as string to join's selectedColumns
      joinAliasMap.get(alias).selectedColumns.push(col);
    } else {
      // Keep as base column
      finalBaseColumns.push(this.parseSelectItem(rawItem));
    }
  } else {
    // Not qualified - keep as base
    finalBaseColumns.push(this.parseSelectItem(rawItem));
  }
});

this.selectedColumns = finalBaseColumns;

  // WHERE binding (top-level)
  p.whereTopNonGrouped.forEach((b, idx) => {
    const c = this.parseSingleConditionBestEffort(b.text);
    c.column = this.normalizeColumnRef(c.column);
    if (idx > 0 && b.opBefore) c.operator = b.opBefore;
    this.nonGroupedConditions.push(c);
  });
  if (this.nonGroupedConditions.length) this.nonGroupedConditions[0].operator = undefined;

  p.whereTopGroups.forEach((b) => {
    const inner = this.unwrapOuterParens(b.text);
    const parts = this.splitTopLevelLogical(inner);

    const group: ConditionGroup = { groupOperator: b.opBefore, conditions: [] };
    parts.forEach((pt, idx) => {
      const c = this.parseSingleConditionBestEffort(pt.text);
      c.column = this.normalizeColumnRef(c.column);
      if (idx > 0 && pt.opBefore) c.operator = pt.opBefore;
      group.conditions.push(c);
    });
    if (group.conditions.length) group.conditions[0].operator = undefined;

    this.groups.push(group);
  });

  // ✅ Auto-bind JOIN objectId + load JOIN attributes
  this.selectedJoins.forEach((j: any, idx: number) => {
    const joinTableClean = this.cleanTableName(j.table);
    const foundJoin = (this.tables || []).find(t =>
      (t.objectName || '').trim().toLowerCase() === joinTableClean
    );
    if (foundJoin?.objectId) {
      j.objectId = foundJoin.objectId;
      this.onJoinObjectSelectionChange({ value: foundJoin.objectId }, idx);
    }
  });

  // ✅ Load base table columns
  const baseTableClean = (p.baseTable || '')
    .replace(/`/g, '')
    .split('.')
    .pop()
    ?.trim()
    .toLowerCase();

  const found = (this.tables || []).find(t =>
    (t.objectName || '').trim().toLowerCase() === baseTableClean
  );

  if (found?.objectId) {
    this.selectedObject = found.objectId;
    this.onObjectSelectionChange({ value: found.objectId });
  }

  this.generateQuery();
}

private cleanTableName(table: string): string {
  return (table || '')
    .replace(/`/g, '')
    .split('.')
    .pop()
    ?.trim()
    .toLowerCase() || '';
}
  /* ============================================================
   * Parsing helpers
   * ============================================================ */

  private normalizeSql(sql: string): string {
    return (sql || '')
      .replace(/\u200B/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/;$/, '');
  }

  private unwrapOuterParens(s: string): string {
    const t = s.trim();
    if (t.startsWith('(') && t.endsWith(')')) {
      return t.slice(1, -1).trim();
    }
    return t;
  }

private splitTopLevelSetOperators(sql: string): Array<{ op?: SetOp; sql: string }> {
  const s = (sql || '').trim();
  const upper = s.toUpperCase();

  let depth = 0;
  let inQuote = false;

  const parts: Array<{ op?: SetOp; sql: string }> = [];
  let last = 0;
  let pendingOp: SetOp | undefined;

  const isBoundaryAfter = (posAfterKeyword: number): boolean => {
    // allow: space, newline, tab, '('
    const ch = upper[posAfterKeyword] || '';
    return ch === '' || /\s|\(/.test(ch);
  };

  const matchAt = (i: number): { op: SetOp; len: number } | null => {
    // we are called ONLY when depth==0 and not inQuote
    // allow optional leading spaces at i
    if (upper.startsWith('UNION ALL', i) && isBoundaryAfter(i + 'UNION ALL'.length)) return { op: 'UNION ALL', len: 'UNION ALL'.length };
    if (upper.startsWith('UNION', i) && isBoundaryAfter(i + 'UNION'.length)) return { op: 'UNION', len: 'UNION'.length };
    if (upper.startsWith('INTERSECT', i) && isBoundaryAfter(i + 'INTERSECT'.length)) return { op: 'INTERSECT', len: 'INTERSECT'.length };
    if (upper.startsWith('MINUS', i) && isBoundaryAfter(i + 'MINUS'.length)) return { op: 'MINUS', len: 'MINUS'.length };
    return null;
  };

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];

    if (ch === "'" && s[i - 1] !== '\\') inQuote = !inQuote;

    if (!inQuote) {
      if (ch === '(') depth++;
      else if (ch === ')') depth = Math.max(0, depth - 1);
    }

    if (!inQuote && depth === 0) {
      // skip spaces: if we are at a space, check the next non-space position too
      let j = i;
      while (j < s.length && /\s/.test(s[j])) j++;

      const m = matchAt(j);
      if (m) {
        const block = s.substring(last, j).trim();
        if (block) parts.push({ op: pendingOp, sql: block });

        pendingOp = m.op;

        // advance last to after the keyword
        last = j + m.len;
        i = last - 1; // continue after keyword
      }
    }
  }

  const tail = s.substring(last).trim();
  if (tail) parts.push({ op: pendingOp, sql: tail });

  // first piece should not have an operator
  if (parts.length && parts[0].op) parts[0].op = undefined;

  return parts;
}


  private wrapAsUnionPiece(op: SetOp, selectSql: string): string {
    const normalized = this.normalizeSql(selectSql);
    const body = normalized.trim().startsWith('(') ? normalized.trim() : `(${normalized.trim()})`;
    return `\n${op} \n${body}`;
  }

 private parseSingleSelect(selectSql: string): ParsedSelect | null {
  const sql = this.normalizeSql(selectSql);
  const upper = sql.toUpperCase();

  const selectIdx = this.indexOfKeywordTopLevel(upper, 'SELECT');
  if (selectIdx === -1) return null;

  const fromIdx = this.indexOfKeywordTopLevel(upper, 'FROM', selectIdx);
  if (fromIdx === -1) return null;

  const whereIdx = this.indexOfKeywordTopLevel(upper, 'WHERE', fromIdx);
  const groupByIdx = this.indexOfGroupByTopLevel(upper, fromIdx);
 // ✅ Decide boundaries correctly
const endOfFrom = [whereIdx, groupByIdx]
  .filter(i => i !== -1)
  .sort((a, b) => a - b)[0] ?? sql.length;

const selectPart = sql.substring(selectIdx + 6, fromIdx).trim();
const fromPart = sql.substring(fromIdx + 4, endOfFrom).trim();

//WHERE should stop at GROUP BY (if exists)
const wherePart =
  whereIdx === -1
    ? ''
    : sql.substring(
        whereIdx + 5,
        groupByIdx !== -1 ? groupByIdx : sql.length
      ).trim();


  let groupByPart = '';

  if (groupByIdx !== -1) {
    const afterGroup = sql.substring(groupByIdx);

    const match = afterGroup.match(/^GROUP\s+BY\s+/i);

    if (match) {
      groupByPart = afterGroup.substring(match[0].length).trim();
    }
  }

  // ✅ IMPORTANT: declare rawSelectItems BEFORE any early return
  const rawSelectItems = this.splitTopLevelComma(selectPart)
    .map(x => x.trim())
    .filter(Boolean);

  // ✅ 1) Try normal FROM table + joins
  const fromParsed = this.parseFromWithJoins(fromPart);

  // ✅ 2) If not parsed, try FROM (subquery) alias
  if (!fromParsed) {
    const sub = this.parseFromSubquery(fromPart);
    if (!sub) return null;

    // WHERE parsing still works (outer WHERE)
    const whereBlocks = wherePart ? this.splitTopLevelLogical(wherePart) : [];
    const whereTopNonGrouped: Array<{ opBefore?: LogicOp; text: string }> = [];
    const whereTopGroups: Array<{ opBefore?: LogicOp; text: string }> = [];

    whereBlocks.forEach(b => {
      const t = b.text.trim();
      if (t.startsWith('(') && t.endsWith(')')) whereTopGroups.push(b);
      else whereTopNonGrouped.push(b);
    });

    return {
      rawSelectItems,
      baseTable: 'addFromQuery',          // ✅ tells UI it is subquery FROM
      baseAlias: sub.alias || '',         // outer alias (a1)
      joins: [],
      wherePart,
      whereTopNonGrouped,
      whereTopGroups,
      groupByPart,

      // ✅ extra fields used by bindParsedSelect
      fromIsSubquery: true,
      fromSubquerySql: sub.subquerySql
    } as any;
  }

  // ✅ Normal WHERE parsing
  const whereBlocks = wherePart ? this.splitTopLevelLogical(wherePart) : [];
  const whereTopNonGrouped: Array<{ opBefore?: LogicOp; text: string }> = [];
  const whereTopGroups: Array<{ opBefore?: LogicOp; text: string }> = [];

  whereBlocks.forEach(b => {
    const t = b.text.trim();
    if (t.startsWith('(') && t.endsWith(')')) whereTopGroups.push(b);
    else whereTopNonGrouped.push(b);
  });

  return {
    rawSelectItems,
    baseTable: fromParsed.baseTable,
    baseAlias: fromParsed.baseAlias,
    joins: fromParsed.joins,
    wherePart,
    whereTopNonGrouped,
    whereTopGroups,
    groupByPart   
  };
}


  private parseFromWithJoins(fromPart: string):
  { baseTable: string; baseAlias: string; joins: ParsedJoin[] } | null {

  const fp = (fromPart || '').trim();
  if (!fp) return null;
  if (fp.startsWith('(')) return null; // FROM (subquery) not supported

  const upper = fp.toUpperCase();

  // 1) Split base FROM (table + alias) from the first JOIN keyword (if any)
  const firstJoinIdx = this.findFirstJoinIndex(upper);
  const baseText = (firstJoinIdx >= 0 ? fp.substring(0, firstJoinIdx) : fp).trim();
  const restText = (firstJoinIdx >= 0 ? fp.substring(firstJoinIdx) : '').trim();

  const base = this.parseTableAndAlias(baseText);
  if (!base) return null;

  // 2) Parse joins using a global regex with lookahead
  const joins: ParsedJoin[] = [];
  if (restText) {
    const joinRe =
      /\b(INNER\s+JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|FULL\s+JOIN|JOIN)\s+([`"\w.]+)\s+(?:AS\s+)?(\w+)\s+ON\s+([\s\S]+?)(?=\b(INNER\s+JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|FULL\s+JOIN|JOIN|WHERE|GROUP\s+BY|ORDER\s+BY|HAVING|UNION|INTERSECT|MINUS)\b|$)/gi;

    let m: RegExpExecArray | null;
    while ((m = joinRe.exec(restText)) !== null) {
      const rawType = (m[1] || '').toUpperCase().replace(/\s+/g, ' ').trim();
      const type: any = (rawType === 'JOIN') ? 'INNER JOIN' : rawType;

      const table = (m[2] || '').replace(/`|"/g, '').trim();
      const alias = (m[3] || '').trim();
      const on = (m[4] || '').trim();

      joins.push({ type, table, alias, on });
    }
  }

  return { baseTable: base.table, baseAlias: base.alias, joins };
}
private findFirstJoinIndex(upperSql: string): number {
  // Finds earliest index of any join keyword (supports newlines/tabs)
  const re = /\b(INNER\s+JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|FULL\s+JOIN|JOIN)\b/;
  const m = re.exec(upperSql);
  return m ? m.index : -1;
}


  private splitFromByJoins(fromText: string): string[] {
  const out: string[] = [];
  let buf = '';
  let depth = 0;
  let inQuote = false;

  const upper = fromText.toUpperCase();

  const isJoinStart = (i: number) => {
    // must be at word boundary
    const prev = i > 0 ? upper[i - 1] : ' ';
    if (/\w/.test(prev)) return false;

    // allow whitespace/newlines between words
    const tail = upper.substring(i);

    return (
      /^INNER\s+JOIN\b/.test(tail) ||
      /^LEFT\s+JOIN\b/.test(tail) ||
      /^RIGHT\s+JOIN\b/.test(tail) ||
      /^FULL\s+JOIN\b/.test(tail) ||
      /^JOIN\b/.test(tail)
    );
  };

  for (let i = 0; i < fromText.length; i++) {
    const ch = fromText[i];

    if (ch === "'" && fromText[i - 1] !== '\\') inQuote = !inQuote;

    if (!inQuote) {
      if (ch === '(') depth++;
      else if (ch === ')') depth = Math.max(0, depth - 1);
    }

    if (!inQuote && depth === 0 && isJoinStart(i)) {
      if (buf.trim()) out.push(buf.trim());

      const rest = fromText.substring(i).trim();
      return [...out, ...this.splitJoinChain(rest)];
    }

    buf += ch;
  }

  if (buf.trim()) out.push(buf.trim());
  return out;
}


 private splitJoinChain(joinText: string): string[] {
  const out: string[] = [];
  let buf = '';
  let depth = 0;
  let inQuote = false;

  const upper = joinText.toUpperCase();

  const isJoinStart = (i: number) => {
    const prev = i > 0 ? upper[i - 1] : ' ';
    if (/\w/.test(prev)) return false;

    const tail = upper.substring(i);
    return (
      /^INNER\s+JOIN\b/.test(tail) ||
      /^LEFT\s+JOIN\b/.test(tail) ||
      /^RIGHT\s+JOIN\b/.test(tail) ||
      /^FULL\s+JOIN\b/.test(tail) ||
      /^JOIN\b/.test(tail)
    );
  };

  for (let i = 0; i < joinText.length; i++) {
    const ch = joinText[i];

    if (ch === "'" && joinText[i - 1] !== '\\') inQuote = !inQuote;

    if (!inQuote) {
      if (ch === '(') depth++;
      else if (ch === ')') depth = Math.max(0, depth - 1);
    }

    if (!inQuote && depth === 0 && buf.length > 0 && isJoinStart(i)) {
      out.push(buf.trim());
      buf = '';
    }

    buf += ch;
  }

  if (buf.trim()) out.push(buf.trim());
  return out;
}


private parseJoinBlock(joinBlock: string): ParsedJoin | null {
  const jb = (joinBlock || '').trim();
  if (!jb) return null;

  // ✅ Robust regex:
  // - supports newlines/spaces anywhere
  // - supports "LEFT JOIN", "INNER JOIN", and plain "JOIN"
  // - supports optional AS for alias
  // - captures ON condition until end of this join block
  const re =
    /^(INNER\s+JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|FULL\s+JOIN|JOIN)\s+([`"\w.]+)\s+(?:AS\s+)?(\w+)\s+ON\s+([\s\S]+)$/i;

  const m = jb.match(re);
  if (!m) return null;

  const rawType = (m[1] || '').toUpperCase().replace(/\s+/g, ' ').trim();
  const type: any = rawType === 'JOIN' ? 'INNER JOIN' : rawType;

  const table = (m[2] || '').replace(/`|"/g, '').trim();
  const alias = (m[3] || '').trim();
  const on = (m[4] || '').trim();

  return { type, table, alias, on };
}


  private parseTableAndAlias(text: string): { table: string; alias: string } | null {
    const t = (text || '').trim();
    if (!t) return null;

    const parts = t.split(/\s+/).filter(Boolean);
    const table = parts[0];
    if (!table) return null;

    let alias = '';
    if (parts[1]?.toUpperCase() === 'AS' && parts[2]) alias = parts[2];
    else if (parts[1]) alias = parts[1];

    return { table, alias };
  }

  private splitTopLevelLogical(wherePart: string): Array<{ opBefore?: LogicOp; text: string }> {
    const out: Array<{ opBefore?: LogicOp; text: string }> = [];
    const upper = wherePart.toUpperCase();

    let buf = '';
    let depth = 0;
    let inQuote = false;
    let pendingOp: LogicOp | undefined;

    for (let i = 0; i < wherePart.length; i++) {
      const ch = wherePart[i];
      if (ch === "'" && wherePart[i - 1] !== '\\') inQuote = !inQuote;

      if (!inQuote) {
        if (ch === '(') depth++;
        else if (ch === ')') depth = Math.max(0, depth - 1);

        if (depth === 0) {
          if (upper.startsWith(' AND ', i) || upper.startsWith(' OR ', i)) {
            const op: LogicOp = upper.startsWith(' AND ', i) ? 'AND' : 'OR';
            if (buf.trim()) out.push({ opBefore: pendingOp, text: buf.trim() });
            buf = '';
            pendingOp = op;
            i += op.length + 1;
            continue;
          }
        }
      }
      buf += ch;
    }

    if (buf.trim()) out.push({ opBefore: pendingOp, text: buf.trim() });
    if (out.length && out[0].opBefore) out[0].opBefore = undefined;
    return out;
  }

  private splitTopLevelComma(part: string): string[] {
    const out: string[] = [];
    let buf = '';
    let depth = 0;
    let inQuote = false;

    for (let i = 0; i < part.length; i++) {
      const ch = part[i];
      if (ch === "'" && part[i - 1] !== '\\') inQuote = !inQuote;

      if (!inQuote) {
        if (ch === '(') depth++;
        else if (ch === ')') depth = Math.max(0, depth - 1);

        if (ch === ',' && depth === 0) {
          out.push(buf);
          buf = '';
          continue;
        }
      }
      buf += ch;
    }
    if (buf.trim()) out.push(buf);
    return out;
  }

  private cleanSelectExprToColumn(expr: string): string {
    const e = (expr || '').trim();
    // remove "AS alias"
    const m = e.match(/^(.*?)(\s+AS\s+|\s+as\s+)(.+)$/);
    if (m) return m[1].trim();

    // remove trailing alias without AS (best-effort)
    const parts = e.split(/\s+/);
    if (parts.length >= 2 && !parts[0].includes('(')) return parts[0].trim();

    return e;
  }

 private parseSingleConditionBestEffort(text: string): Condition {
  const t = (text || '').trim();

  // Handles: col = 'x' | col = 10 | alias.col = 10 | col >= 10 | etc.
  const m = t.match(/^(.+?)\s*(=|!=|<>|>=|<=|>|<|LIKE|IN|IS\s+NULL|IS\s+NOT\s+NULL)\s*(.*)$/i);

  if (!m) {
    return { column: '', conditionalOperator: '=', value: t };
  }

  let column = this.normalizeColumnRef(m[1]);
  let op = m[2].toUpperCase().replace(/\s+/g, ' ').trim();
  let value = (m[3] || '').trim();

  // IS NULL / IS NOT NULL has no value
  if (op === 'IS NULL' || op === 'IS NOT NULL') value = '';

  // Remove surrounding quotes for UI display
  if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
    value = value.substring(1, value.length - 1);
  }

  // IN (..)
  if (op === 'IN') {
    value = value.replace(/^\(/, '').replace(/\)$/, '').trim();
  }

  return { column, conditionalOperator: op as any, value };
}

private normalizeColumnRef(col: string): string {
  let c = (col || '').trim();

  // remove wrapping quotes/backticks
  c = c.replace(/^[`"]|[`"]$/g, '');

  // if alias.column => keep only column
  const m = c.match(/^([A-Za-z_][\w]*)\.([A-Za-z_][\w]*)$/);
  if (m) return m[2];

  return c;
}

  private indexOfKeywordTopLevel(upperSql: string, keyword: string, startIdx: number = 0): number {
    let depth = 0;
    let inQuote = false;

    for (let i = startIdx; i <= upperSql.length - keyword.length; i++) {
      const ch = upperSql[i];
      if (ch === "'" && upperSql[i - 1] !== '\\') inQuote = !inQuote;
      if (inQuote) continue;

      if (ch === '(') depth++;
      else if (ch === ')') depth = Math.max(0, depth - 1);

      if (depth === 0 && upperSql.startsWith(keyword, i)) return i;
    }
    return -1;
  }

  private parseFromSubquery(fromPart: string): { subquerySql: string; alias: string } | null {
  const fp = (fromPart || '').trim();
  if (!fp.startsWith('(')) return null;

  let depth = 0;
  let inQuote = false;
  let endIdx = -1;

  for (let i = 0; i < fp.length; i++) {
    const ch = fp[i];
    if (ch === "'" && fp[i - 1] !== '\\') inQuote = !inQuote;

    if (!inQuote) {
      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth === 0) { endIdx = i; break; }
      }
    }
  }

  if (endIdx === -1) return null;

  const inner = fp.substring(1, endIdx).trim();   // inside (...)
  const tail = fp.substring(endIdx + 1).trim();   // alias part

  let alias = '';
  const m = tail.match(/^(?:AS\s+)?([A-Za-z_]\w*)/i);
  if (m) alias = m[1];

  return { subquerySql: inner, alias };
}

addSelectItem(): void {
  this.selectedColumns.push({
    type: 'column',
    column: '',
    aggregate: '',
    distinct: false,
    alias: ''
  });
  // Do not auto-generate; user will fill fields
}

removeSelectItem(index: number): void {
  this.selectedColumns.splice(index, 1);
  this.generateQuery();
}

private parseSelectItem(rawExpr: string): SelectItem {
  const trimmed = rawExpr.trim();
  // Extract alias if present (AS alias)
  const aliasMatch = trimmed.match(/\s+AS\s+([^\s]+)$/i);
  const alias = aliasMatch ? aliasMatch[1] : undefined;
  let exprWithoutAlias = aliasMatch ? trimmed.substring(0, aliasMatch.index).trim() : trimmed;

  // Check for aggregate pattern: FUNC(DISTINCT? ...)
  const aggMatch = exprWithoutAlias.match(/^(\w+)\s*\(\s*(DISTINCT\s+)?(.+?)\s*\)$/i);
  if (aggMatch) {
    const func = aggMatch[1].toUpperCase();
    const distinct = !!aggMatch[2];
    let inner = aggMatch[3].trim();
    // Remove table alias if present
    inner = inner.replace(/^[^.]+\./, '');
    return {
      type: 'column',
      column: inner,
      aggregate: func,
      distinct: distinct,
      alias: alias
    };
  }

  // Check if it's a simple column (possibly with table alias)
  const colMatch = exprWithoutAlias.match(/^[\w.*]+$/);
  if (colMatch) {
    let col = exprWithoutAlias;
    // If it's alias.col, strip alias
    const dotParts = col.split('.');
    if (dotParts.length === 2) col = dotParts[1];
    return {
      type: 'column',
      column: col,
      alias: alias
    };
  }

  // Otherwise treat as expression
  return {
    type: 'expression',
    expression: exprWithoutAlias,
    alias: alias
  };
}

private qualifyColumn(colName: string, baseQualifier: string): string {
  if (!colName) return '';
  if (colName.includes('.')) return colName; // already qualified or expression
  return baseQualifier ? `${baseQualifier}${colName}` : colName;
}

private indexOfGroupByTopLevel(sql: string, startIdx: number = 0): number {
  let depth = 0;
  let inQuote = false;

  for (let i = startIdx; i < sql.length; i++) {
    const ch = sql[i];

    if (ch === "'" && sql[i - 1] !== '\\') inQuote = !inQuote;
    if (inQuote) continue;

    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);

    if (depth === 0) {
      const rest = sql.substring(i);

      // flexible match: GROUP + spaces + BY
      const match = rest.match(/^GROUP\s+BY\b/);
      if (match) {
        return i;
      }
    }
  }

  return -1;
}


}
